import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import * as React from "react";
import cn from "twsx";

const controlGroupVariants = cva(
  "flex items-center",
  {
    variants: {
      /*
       * spaced   — controls sit side-by-side with a gap between them. Each
       *            control keeps its own border-radius. Use for toolbars,
       *            search + action rows, filter chips next to a button.
       *
       * connected — controls share a continuous border. Adjacent edges are
       *             flattened so the group looks like a single compound control.
       *             Use for prefix-select + input, split-buttons, segmented
       *             controls where the grouping carries meaning.
       */
      variant: {
        spaced: "gap-sm",
        connected: [
          // Flatten inner edges and collapse the double-border
          "[&>*:not(:first-child):not(:last-child)]:rounded-none",
          "[&>*:first-child:not(:last-child)]:rounded-r-none",
          "[&>*:last-child:not(:first-child)]:rounded-l-none",
          // Collapse adjacent left border into the previous control's right border
          "[&>*:not(:first-child)]:-ml-px",
          // Bring focused/hovered controls to front so their ring is unclipped
          "[&>*]:relative [&>*:hover]:z-10 [&>*:focus-within]:z-20",
        ],
      },
      /*
       * stretch — each child fills equal width (useful for segmented controls
       *           or form rows where you want uniform column widths).
       */
      stretch: {
        true: "[&>*]:flex-1",
        false: null,
      },
    },
    defaultVariants: {
      variant: "spaced",
      stretch: false,
    },
  },
);

export type ControlGroupProps =
  & React.HTMLAttributes<HTMLDivElement>
  & VariantProps<typeof controlGroupVariants>
  & {
    ref?: React.Ref<HTMLDivElement>;
  };

/** Use when several controls sit side by side as one joined unit. */
export function ControlGroup({
  className,
  variant,
  stretch,
  ref,
  ...props
}: ControlGroupProps) {
  return (
    <div
      ref={ref}
      className={cn(controlGroupVariants({ variant, stretch }), className)}
      {...props}
    />
  );
}
ControlGroup.displayName = "ControlGroup";
